import {
  clearAuthentication,
  hasValidAuthentication,
  redirectToLogin,
} from './auth';

// Get the current hostname to determine if we're in development or production
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE_URL = isDevelopment 
  ? process.env.REACT_APP_API_URL || 'http://localhost:8080/api'  // Use environment variable with fallback
  : '/api'; // In production, use relative path which will be proxied by nginx

/**
 * A wrapper around the native fetch function that automatically adds
 * the JWT Authorization header if a token is found in localStorage.
 * It now also parses the JSON response for successful requests and 
 * throws a more detailed error for failed requests.
 *
 * @param {string} endpoint The API endpoint (e.g., '/users/me')
 * @param {object} options Fetch options (method, body, headers, etc.)
 * @param {boolean} [options.isPublic=false] - Set to true if the endpoint is public and should not include Authorization header.
 * @returns {Promise<any>} The parsed JSON data from the response if successful.
 * @throws {Error} Throws an error with status and message if the request fails.
 */
export const fetchWithAuth = async (endpoint, options = {}) => {
  const { isPublic, ...restOptions } = options; // Destructure isPublic
  const token = isPublic
    ? null
    : (hasValidAuthentication() ? localStorage.getItem('jwtToken') : null);

  // Don't set default Content-Type if body is FormData
  const isFormData = restOptions.body instanceof FormData;
  
  const defaultHeaders = isFormData ? {} : {
    'Content-Type': 'application/json',
  };

  const headers = {
    ...defaultHeaders,
    ...restOptions.headers,
  };

  if (token && !isPublic) { // Only add token if not a public endpoint
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Only stringify body if it's an object and NOT FormData and Content-Type is application/json
  let body = restOptions.body;
  if (body && typeof body === 'object' && !isFormData && headers['Content-Type'] === 'application/json') {
    body = JSON.stringify(body);
  }

  const url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;

  try {
    const response = await fetch(url, {
      ...restOptions, // Use restOptions here
      headers,
      body, // Use the potentially stringified body
    });

    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({ message: 'Unauthorized' }));

      // A 401 from a protected endpoint means the server did not accept the
      // current session. The server remains authoritative; clear all cached
      // identity data and return the user to the page they originally wanted.
      if (!isPublic) {
        clearAuthentication();
        redirectToLogin();
      }

      throw new Error(errorData.message || `Unauthorized (401)`);
    }

    if (!response.ok) {
      // Try to parse error details from the response body
      const errorData = await response.json().catch(() => ({ message: `HTTP error! Status: ${response.status}` }));
      if (response.status === 403 && errorData.code === 'AGE_CONFIRMATION_REQUIRED') {
        const language = window.location.pathname.split('/')[1] || 'mk';
        const profilePath = `/${language}/my-profile`;
        if (window.location.pathname !== profilePath) window.location.assign(profilePath);
      }
      // Prefer message from errorData if available
      const errorMessage = errorData.message || `HTTP error! Status: ${response.status}`;
      console.error('API Error:', errorMessage, 'Status:', response.status);
      throw new Error(errorMessage);
    }

    // If response is OK, parse and return JSON data
    // Handle cases where response might be empty (e.g., 204 No Content)
    if (response.status === 204) {
        return null; // Or undefined, or an empty object, depending on expected behavior
    }
    return await response.json();

  } catch (error) {
    // Log the error if it hasn't been logged already (e.g. network error)
    if (!(error.message.includes('HTTP error!') || error.message.includes('Unauthorized'))) {
        console.error('API call failed (e.g., network error):', error);
    }
    // Re-throw the error so the calling component can handle it specifically
    throw error;
  }
};

// Example usage for a GET request:
// async function fetchUserProfile() {
//   try {
//     const response = await fetchWithAuth('/users/me'); // Assuming '/users/me' is a protected endpoint
//     if (!response.ok) {
//       throw new Error(`HTTP error! status: ${response.status}`);
//     }
//     const data = await response.json();
//     console.log('User profile:', data);
//   } catch (error) {
//     console.error('Failed to fetch user profile:', error);
//   }
// }

// Example usage for a POST request:
// async function createPost(postData) {
//   try {
//     const response = await fetchWithAuth('/posts', {
//       method: 'POST',
//       body: JSON.stringify(postData),
//     });
//     if (!response.ok) {
//        const errorData = await response.json().catch(() => ({ message: 'Failed to create post and parse error failed.' }));
//        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
//     }
//     const data = await response.json();
//     console.log('Post created:', data);
//     return data;
//   } catch (error) {
//     console.error('Failed to create post:', error);
//     throw error; // Re-throw to allow component-level handling
//   }
// }
